import { receiveOrderHistorySearch } from './orders';

import { PROVIDE_LABEL_OPTIONS } from 'constants/exchanges';
import {
  CLEAR_VIEWED_ORDER,
  ORDER_CLEAR_SELECTED_ITEMS,
  RECEIVE_ADD_NEW_BILLING_ADDRESS_ERROR,
  RECEIVE_ADD_NEW_PAYMENT_ERROR,
  RECEIVE_ADD_NEW_PAYMENT_SUCCESS,
  RECEIVE_BILLING_ADDRESSES,
  RECEIVE_CONFIRM_CANCELLATION,
  RECEIVE_CONFIRM_CANCELLATION_ERROR,
  RECEIVE_ORDER_DETAILS,
  RECEIVE_SUGGESTED_ADDRESSES,
  RECEIVE_UPDATE_PAYMENT_ERROR,
  RECEIVE_UPDATE_PAYMENT_SUCCESS,
  REDIRECT,
  REQUEST_ADD_NEW_PAYMENT,
  REQUEST_BILLING_ADDRESSES,
  REQUEST_CONFIRM_CANCELLATION,
  REQUEST_ORDER_DETAILS,
  REQUEST_UPDATE_ASSOCIATED_PAYMENT,
  REQUEST_UPDATE_UNASSOCIATED_PAYMENT,
  SET_IS_ORDER_ITEMS_UPDATING,
  SET_IS_REPETITION_CONFLICT_ERROR,
  SET_IS_SHIPMENT_OPTIONS_ERROR,
  SET_IS_SHIPMENT_OPTIONS_LOADING,
  SET_RETURN_SOURCE_ADDRESS,
  SET_SHIPMENT_OPTION_DETAILS,
  SET_SHIPMENT_OPTIONS,
  TOGGLE_ADD_NEW_BILLING_ADDRESS,
  UPDATE_CANCELLATION_REASON,
  UPDATE_ORDER_WITH_TRACKING_INFO
} from 'constants/reduxActions';
import { prependAppRoot } from 'history/AppRootUtils';
import { fetchShipmentTracking } from 'actions/account/shipmentTracking';
import { authenticationErrorCatchHandlerFactory } from 'actions/errors';
import {
  fetchApiNotAuthenticatedMiddleware,
  fetchErrorMiddleware,
  fetchErrorMiddlewareMaybeJson,
  fetchMafiaValidationErrorMiddleware,
  fetchTransportationOptionErrorMiddleware,
  REPETITIVE_TRANSPORTATION_OPERATION_ERROR
} from 'middleware/fetchErrorMiddleware';
import {
  addresses,
  cancelGuestOrder,
  cancelOrder,
  changeAddress,
  creditCard,
  fetchGuestTransportationOptions,
  fetchTransportationOptions,
  getGuestOrder,
  getOrder,
  savePayment,
  updatePayment
} from 'apis/mafia';
import { setSessionCookies } from 'actions/session';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { selectMafiaConfig } from 'selectors/environment';
import { recordToSplunk } from 'apis/recordToSplunk';
import { returnToAccountPath } from 'constants/appConstants';
import { EMAIL_TOKEN_COOKIE } from 'constants/cookies';
import { trackOperation, trackOperationFailureIfAuthed } from 'events/ops/opsEvent';

export function requestOrderDetails() {
  return {
    type: REQUEST_ORDER_DETAILS
  };
}

export function receiveOrderDetails(order) {
  return {
    type: RECEIVE_ORDER_DETAILS,
    order
  };
}

export function clearViewedOrder() {
  return {
    type: CLEAR_VIEWED_ORDER
  };
}

export function updateCancellationReason(cancellationReasonByLineItem) {
  return {
    type: UPDATE_CANCELLATION_REASON,
    cancellationReasonByLineItem
  };
}

export function clearSelectedItems() {
  return {
    type: ORDER_CLEAR_SELECTED_ITEMS
  };
}

export function requestConfirmCancellation() {
  return {
    type: REQUEST_CONFIRM_CANCELLATION
  };
}

export function receiveConfirmCancellation() {
  return {
    type: RECEIVE_CONFIRM_CANCELLATION
  };
}

export function receiveConfirmCancellationError() {
  return {
    type: RECEIVE_CONFIRM_CANCELLATION_ERROR
  };
}

export function requestUpdateAssociatedPayment() {
  return {
    type: REQUEST_UPDATE_ASSOCIATED_PAYMENT
  };
}

export function receiveUpdatePaymentSuccess() {
  return {
    type: RECEIVE_UPDATE_PAYMENT_SUCCESS
  };
}

export function requestUpdateUnassociatedPayment() {
  return {
    type: REQUEST_UPDATE_UNASSOCIATED_PAYMENT
  };
}

export function receiveUpdatePaymentError() {
  return {
    type: RECEIVE_UPDATE_PAYMENT_ERROR
  };
}

export function requestAddNewPayment() {
  return {
    type: REQUEST_ADD_NEW_PAYMENT
  };
}

export function receiveAddNewPaymentSuccess(newPaymentInstrumentId) {
  return {
    type: RECEIVE_ADD_NEW_PAYMENT_SUCCESS,
    newPaymentInstrumentId
  };
}

export function receiveAddNewPaymentError(error) {
  return {
    type: RECEIVE_ADD_NEW_PAYMENT_ERROR,
    error
  };
}

export function requestBillingAddresses() {
  return {
    type: REQUEST_BILLING_ADDRESSES
  };
}

export function receiveBillingAddresses(addresses) {
  return {
    type: RECEIVE_BILLING_ADDRESSES,
    addresses
  };
}

export function toggleAddNewBillingAddress(visible = false) {
  return {
    type: TOGGLE_ADD_NEW_BILLING_ADDRESS,
    visible
  };
}

export function receiveAddNewBillingAddressError(error) {
  return {
    type: RECEIVE_ADD_NEW_BILLING_ADDRESS_ERROR,
    error
  };
}

export function receiveSuggestedAddresses(suggestedAddresses, newAddressId) {
  return {
    type: RECEIVE_SUGGESTED_ADDRESSES,
    suggestedAddresses,
    newAddressId
  };
}

export function updateOrderWithTrackingInfo(order, trackingInfo) {
  return {
    type: UPDATE_ORDER_WITH_TRACKING_INFO,
    order,
    trackingInfo
  };
}

export function setShipmentOptions(shipmentOptions) {
  return {
    type: SET_SHIPMENT_OPTIONS,
    shipmentOptions
  };
}

export function setShipmentOptionDetails(shipmentOptionDetails) {
  return {
    type: SET_SHIPMENT_OPTION_DETAILS,
    shipmentOptionDetails
  };
}

export function setReturnSourceAddress(returnSourceAddress) {
  return {
    type: SET_RETURN_SOURCE_ADDRESS,
    returnSourceAddress
  };
}

export function setIsShipmentOptionsError(isShipmentOptionsError) {
  return {
    type: SET_IS_SHIPMENT_OPTIONS_ERROR,
    isShipmentOptionsError
  };
}

export function setIsShipmentOptionsLoading(isShipmentOptionsLoading) {
  return {
    type: SET_IS_SHIPMENT_OPTIONS_LOADING,
    isShipmentOptionsLoading
  };
}

export function setIsOrderItemsUpdating(isOrderItemsUpdating) {
  return {
    type: SET_IS_ORDER_ITEMS_UPDATING,
    isOrderItemsUpdating
  };
}

export function setIsRepetitionConflictError(isRepetitionConflictError) {
  return {
    type: SET_IS_REPETITION_CONFLICT_ERROR,
    isRepetitionConflictError
  };
}

export function confirmCancellation(orderId, items, cancelAuthEndpoint = cancelOrder) {
  return (dispatch, getState) => {
    dispatch(requestConfirmCancellation());
    const state = getState();
    const {
      authentication,
      cookies,
      router: { location }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);
    const cancelOrderEndpoint = authentication ? cancelAuthEndpoint : cancelGuestOrder;

    return cancelOrderEndpoint(mafiaConfig, { orderId, items }, cookies)
      .then(processHeadersMiddleware(setSessionCookies, getState))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        if (resp.success) {
          dispatch(receiveConfirmCancellation(resp));
          trackOperation('OrderCancel', 'success');
        } else {
          dispatch(receiveConfirmCancellationError(resp));
          trackOperation('OrderCancel', 'failure');
        }
      })
      .catch(e => {
        trackOperationFailureIfAuthed('OrderCancel', e?.status);
        authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(`/orders/${orderId}`, location))(e);
      });
  };
}

export function fetchOrderDetails(orderId, shouldFetchTracking = false, isMyAccRedesign = false, getAuthOrder = getOrder) {
  return (dispatch, getState) => {
    dispatch(requestOrderDetails());
    const state = getState();
    const { authentication, cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);
    const getOrderEndpoint = authentication && !cookies[EMAIL_TOKEN_COOKIE] ? getAuthOrder : getGuestOrder;
    const isGuest = !authentication || !!cookies[EMAIL_TOKEN_COOKIE];
    const operationName = isGuest ? 'GuestOrderDataLoad' : 'OrderDataLoad';

    return getOrderEndpoint(mafiaConfig, orderId, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        const { orderId, shipments } = resp;
        if (shouldFetchTracking && orderId && shipments) {
          // fetch tracking info for mobile order detail page
          shipments.forEach(shipment => {
            const { id } = shipment;
            dispatch(fetchShipmentTracking(orderId, id));
          });
        }
        dispatch(receiveOrderDetails(resp));
        trackOperation(operationName, 'success');
        return resp;
      })
      .catch(e => {
        trackOperationFailureIfAuthed(operationName, e?.status);
        //not sure if there could be any other status coming here
        if (e.status === 404) {
          const resp = { orders: [], totalPages: 1 };
          dispatch(receiveOrderHistorySearch(resp));
          dispatch({ type: REDIRECT, location: isMyAccRedesign ? `/my-orders/?search=${orderId}` : `/orders?search=${orderId}` });
        } else {
          // if order call fails, redirect user back to main account page
          dispatch({ type: REDIRECT, location: returnToAccountPath });
        }
      });
  };
}

export function updateAssociatedPayment(paymentInstrumentId, updatePaymentEndpoint = updatePayment) {
  return (dispatch, getState) => {
    dispatch(requestUpdateAssociatedPayment());
    const state = getState();
    const {
      cookies,
      router: { location },
      order: {
        order: { orderId }
      }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return updatePaymentEndpoint(mafiaConfig, { orderId, paymentInstrumentId }, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddlewareMaybeJson)
      .then(() => dispatch(receiveUpdatePaymentSuccess()))
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(`/orders/${orderId}`, location)));
  };
}

export function updateUnassociatedPayment(ccNum, paymentInstrumentId, addressId, creditCardEndpoint = creditCard) {
  return (dispatch, getState) => {
    dispatch(requestUpdateUnassociatedPayment());
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return creditCardEndpoint(mafiaConfig, cookies, ccNum, paymentInstrumentId, addressId) // associate payment instrument
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ status }) => {
        if (status) {
          dispatch(updateAssociatedPayment(paymentInstrumentId));
        } else {
          // something went wrong
          dispatch(receiveUpdatePaymentError());
        }
      })
      .catch(() => {
        dispatch(receiveUpdatePaymentError());
      });
  };
}

export function addNewPayment(stateInstrumentData, savePaymentEndpoint = savePayment) {
  return (dispatch, getState) => {
    dispatch(requestAddNewPayment());
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return savePaymentEndpoint(mafiaConfig, stateInstrumentData, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        const { paymentInstrumentId, response } = resp;
        if (paymentInstrumentId) {
          // successful 1st call that generates paymentInstrumentId
          dispatch(receiveAddNewPaymentSuccess(paymentInstrumentId));
          return resp; // for amethyst addPaymentInstrument event
        } else if (response) {
          // successful 2nd call with addressId ('response' key is paymentInstrumentId)
          dispatch(updateAssociatedPayment(response));
        } else {
          // something went wrong
          dispatch(receiveAddNewPaymentError());
        }
      })
      .catch(e => {
        dispatch(receiveAddNewPaymentError(e));
      });
  };
}

export function fetchBillingAddresses(addressesEndpoint = addresses) {
  return (dispatch, getState) => {
    dispatch(requestBillingAddresses());
    const state = getState();
    const {
      cookies,
      router: { location },
      order: {
        order: { orderId }
      }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return addressesEndpoint(mafiaConfig, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ addresses }) => {
        dispatch(receiveBillingAddresses(addresses));
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(`/orders/${orderId}`, location)));
  };
}

export function saveNewBillingAddress(
  stateAddressData,
  ccStateInstrumentData,
  suggestedAddresses,
  addNewPaymentOnSave = true,
  changeAddressEndpoint = changeAddress
) {
  return (dispatch, getState) => {
    const state = getState();
    const {
      cookies,
      order: { newPaymentInstrumentId }
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    const params = {
      mafiaConfig,
      address: stateAddressData,
      forceOriginal: !!suggestedAddresses // force address assuming user has already been shown suggestions
    };
    return changeAddressEndpoint(params, cookies, false) // saveInvalidAddress 3rd param set to false
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        const { valid, addressId, suggestedAddresses, invalidFields } = response;
        if (!valid) {
          if (invalidFields?.length) {
            // mafia api validation error
            dispatch(
              receiveAddNewBillingAddressError({
                extraInformation: invalidFields
              })
            );
          }
          if (suggestedAddresses?.length) {
            // display suggested addresses
            dispatch(receiveSuggestedAddresses(suggestedAddresses, addressId));
          }
        } else if (addressId) {
          // assume everything looks good, addNew Payment
          const updatedInstrumentData = {
            // create deep copy to be safe
            ...ccStateInstrumentData,
            instrument: { ...ccStateInstrumentData.instrument }
          };
          updatedInstrumentData.addressId = addressId;
          updatedInstrumentData.instrument.paymentInstrumentId = newPaymentInstrumentId;
          addNewPaymentOnSave && dispatch(addNewPayment(updatedInstrumentData));
        }
        return response;
      })
      .catch(e => {
        dispatch(receiveAddNewBillingAddressError(e));
      });
  };
}

export const extractOptionNames = eligibleTransportOptions => eligibleTransportOptions.map(option => option.legacyName);

export function orderOptions(options) {
  const optionsSet = new Set(options);

  const rightOrderOptions = [
    PROVIDE_LABEL_OPTIONS.UPS_PACKAGELESS,
    PROVIDE_LABEL_OPTIONS.WFM_PACKAGELESS,
    PROVIDE_LABEL_OPTIONS.LABELLESS,
    PROVIDE_LABEL_OPTIONS.PRINT,
    PROVIDE_LABEL_OPTIONS.EMAIL,
    PROVIDE_LABEL_OPTIONS.PICKUP,
    PROVIDE_LABEL_OPTIONS.MAIL
  ];

  return rightOrderOptions.filter(rightOption => optionsSet.has(rightOption));
}

export function handleTransportationOptionsError(err, dispatch) {
  if (err.id === REPETITIVE_TRANSPORTATION_OPERATION_ERROR) {
    dispatch(setIsRepetitionConflictError(true));
  } else {
    dispatch(setIsShipmentOptionsError(true));
  }
}

export function getTransportationOptions(isReturn = true, fetchAuthTransportationOptions = fetchTransportationOptions) {
  return (dispatch, getState) => {
    const state = getState();
    const {
      authentication,
      cookies,
      router: { location },
      order,
      returns: { contractIds, preReturnInfo, multiPreReturnInfo },
      exchange: {
        preExchangeInfo: { contractId, returnItems }
      }
    } = state;
    const orderId = order.order?.orderId;
    const mafiaConfig = selectMafiaConfig(state);

    dispatch(setShipmentOptions([]));
    dispatch(setReturnSourceAddress(null));
    dispatch(setIsRepetitionConflictError(false));
    dispatch(setIsShipmentOptionsLoading(true));

    const submissionItems = preReturnInfo.length
      ? preReturnInfo[0].returnItems.map(item => ({
          unitId: item.unitId
        }))
      : multiPreReturnInfo
        ? multiPreReturnInfo.documents[0].returnItems.map(({ unitId }) => ({ unitId }))
        : [];

    const requestBody = isReturn
      ? {
          contractId: multiPreReturnInfo ? multiPreReturnInfo.documents[0].contractId : contractIds[0],
          submissionItems: submissionItems
        }
      : {
          contractId: contractId,
          submissionItems: returnItems.map(item => ({ unitId: item.unitId }))
        };

    const fetchTransportationOptionsEndpoint = authentication ? fetchAuthTransportationOptions : fetchGuestTransportationOptions;

    return fetchTransportationOptionsEndpoint(mafiaConfig, requestBody, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchTransportationOptionErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        const resultOptions = orderOptions(extractOptionNames(response.eligibleTransportOptions));
        if (resultOptions.length) {
          dispatch(setIsShipmentOptionsError(false));
          dispatch(setShipmentOptions(resultOptions));
          dispatch(setShipmentOptionDetails(response.eligibleTransportOptions));
          dispatch(setReturnSourceAddress(response.returnSourceAddress));
        } else {
          dispatch(setIsShipmentOptionsError(true));
        }
        dispatch(setIsShipmentOptionsLoading(false));
        trackOperation('ReturnFormDataLoad', 'success');
      })
      .catch(err => {
        trackOperationFailureIfAuthed('ReturnFormDataLoad', err?.status);
        recordToSplunk(
          `type=fetchTransportationOptionsApi&error=${encodeURIComponent(err.toString())}&details=${encodeURIComponent(err.detailedMessage)}`
        );
        handleTransportationOptionsError(err, dispatch);
        authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(orderId ? `/orders/${orderId}` : '/my-orders', location));
        dispatch(setIsShipmentOptionsLoading(false));
      });
  };
}
